/**
 * Превью контура — то, что открывается нажатием на полигон.
 *
 * Раньше нажатие уносило с карты на карточку контура: человек терял кадр,
 * масштаб и разобранные фильтры ради четырёх строк, которые карта и так знает.
 * Теперь нажатие отвечает на вопрос «что это за контур» на месте, а полный
 * уровень остаётся одним нажатием ниже — и обычной ссылкой, которую можно
 * скопировать и открыть в новой вкладке.
 *
 * Правила, которые превью держит вместе с картой:
 *
 * * **величины те же, что в подсказке под курсором.** Обе собираются
 *   `contourTip`: превью и подсказка не имеют права разойтись в словах;
 * * **площадь каноническая.** Площадь по геометрии NGIS с ней не сходится и
 *   ни с чем здесь не складывается;
 * * **синтетика помечена.** Культура контура приходит из демо-каскада, и её
 *   пометка едет вместе с ней, а не остаётся на плашке карты;
 * * **отсутствие названо.** «Землепользователь не задан» — это отметка, а не
 *   пустая строка;
 * * **переходы уровня приходят слотом.** Ссылки живут в `links`, а не внутри
 *   карточки: тогда карточку можно показать и там, где маршрутизатора нет, а
 *   переход собирает тот экран, который знает свой контекст анализа.
 */

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { ContourFeatureProps } from "../../api/client";
import { Badge, Button } from "../../components/ui";
import { levelPath } from "../../lib/levels";
import { ROUTES } from "../../lib/routes";
import { defineMessages } from "../../i18n/define";
import { useT } from "../../i18n/hooks";
import { contourTip } from "./mapModel";
import styles from "./map.module.css";

const m = defineMessages({
  noLandUser: {
    id: "datamap.preview.noLandUser",
    defaultMessage: "землепользователь не задан",
    description:
      "Бейдж в превью контура на карте (хоким, менеджер): в реестре у контура не указан землепользователь; строчная, бейдж",
  },
  noLandUserHint: {
    id: "datamap.preview.noLandUserHint",
    defaultMessage: "В реестре у контура нет землепользователя",
    description: "Подсказка при наведении на бейдж «землепользователь не задан» в превью контура",
  },
  sharedBadge: {
    id: "datamap.preview.sharedBadge",
    defaultMessage: "общая геометрия: {count}",
    description:
      "Бейдж в превью контура: один и тот же полигон показан у нескольких контуров; {count} — сколько контуров делят полигон; строчная, бейдж",
  },
  sharedHint: {
    id: "datamap.preview.sharedHint",
    defaultMessage: "Тот же полигон показан у нескольких контуров",
    description: "Подсказка при наведении на бейдж «общая геометрия» в превью контура",
  },
  demoCrop: {
    id: "datamap.preview.demoCrop",
    defaultMessage: "ДЕМО: культура",
    description:
      "Бейдж в превью контура: культура на контуре синтетическая, не из реестра; ДЕМО заглавными, бейдж",
  },
  tierBadge: {
    id: "datamap.preview.tierBadge",
    defaultMessage: "ярус: {tier}",
    description:
      "Бейдж в превью контура: ярус принятой связи геометрии с контуром; {tier} — подпись яруса («автоматический»); строчная, бейдж",
  },
  tierHint: {
    id: "datamap.preview.tierHint",
    defaultMessage: "Ярус принятой связи с NGIS-2024",
    description: "Подсказка при наведении на бейдж яруса в превью контура; NGIS-2024 — название источника геометрии, не переводить",
  },
  massif: {
    id: "datamap.preview.massif",
    defaultMessage: "Массив",
    description: "Название строки в превью контура на карте: массив, в котором лежит контур",
  },
  landUser: {
    id: "datamap.preview.landUser",
    defaultMessage: "Землепользователь",
    description: "Название строки в превью контура на карте: хозяйство, за которым записан контур",
  },
  registryArea: {
    id: "datamap.preview.registryArea",
    defaultMessage: "Площадь реестра",
    description: "Название строки в превью контура на карте: каноническая площадь из реестра, в гектарах",
  },
  relation: {
    id: "datamap.preview.relation",
    defaultMessage: "Отношение геометрии",
    description:
      "Название строки в превью контура на карте: как полигон NGIS соотносится с контуром («фича = контур»)",
  },
  pieces: {
    id: "datamap.preview.pieces",
    defaultMessage: "фич: {count}",
    description:
      "Дописка к отношению геометрии в превью контура: из скольких фич NGIS собран полигон; ставится после « · »; {count} — число",
  },
  cropNow: {
    id: "datamap.preview.cropNow",
    defaultMessage: "Культура сейчас (ДЕМО)",
    description:
      "Название строки в превью контура: культура сезона на контуре, данные синтетические (ДЕМО заглавными)",
  },
  cropCount: {
    id: "datamap.preview.cropCount",
    defaultMessage: "культур на контуре: {count}",
    description:
      "Дописка к строке «Культура сейчас» в превью контура: на контуре несколько культур; ставится после « · »; {count} — число",
  },
  note: {
    id: "datamap.preview.note",
    defaultMessage:
      "Площадь — каноническая, из реестра. Площадь по геометрии NGIS с ней не сходится и здесь ни с чем не складывается.",
    description:
      "Пояснение внизу превью контура: почему площадь берётся из реестра, а не считается по полигону; NGIS — источник геометрии, не переводить",
  },
  noteShared: {
    id: "datamap.preview.noteShared",
    defaultMessage: "Показанный полигон принадлежит нескольким контурам.",
    description:
      "Вторая фраза пояснения внизу превью контура (только если полигон общий для нескольких контуров); идёт после основного пояснения через пробел",
  },
  open: {
    id: "datamap.preview.open",
    defaultMessage: "Открыть карточку",
    description: "Кнопка в закреплённой нижней строке панели превью контура: перейти к полной карточке контура",
  },
  massifLink: {
    id: "datamap.preview.massifLink",
    defaultMessage: "Массив: {name}",
    description:
      "Ссылка в превью контура на карточку массива; {name} — имя массива либо «#номер», если имя не загружено",
  },
  noOneToFind: {
    id: "datamap.preview.noOneToFind",
    defaultMessage: "землепользователя нет — искать некого",
    description:
      "Пояснение вместо ссылки в превью контура: у контура нет землепользователя, поиск по реестру бессмыслен; строчная",
  },
  farmLink: {
    id: "datamap.preview.farmLink",
    defaultMessage: "Хозяйство «{name}» в реестре",
    description:
      "Ссылка в превью контура: найти хозяйство в реестре хозяйств; {name} — название хозяйства из данных, в кавычках",
  },
  nameMissing: {
    id: "datamap.preview.nameMissing",
    defaultMessage: "землепользователей: {count} — имени в слое карты нет",
    description:
      "Пояснение вместо ссылки в превью контура: у контура несколько землепользователей, а слой карты имени не отдаёт; {count} — их число; строчная",
  },
  oneOfMany: {
    id: "datamap.preview.oneOfMany",
    defaultMessage: "у контура их {count}: слой называет одного",
    description:
      "Пояснение в превью контура: землепользователей несколько, а в слое карты назван только один; {count} — число землепользователей; строчная",
  },
});

export function MapPreviewCard({
  props,
  massifName,
  links,
}: {
  props: ContourFeatureProps;
  /** Имя массива из справочника. `null` — покажем честный `массив #id`. */
  massifName: string | null;
  /** Переходы на соседние уровни. Собирает экран: адреса знает он. */
  links?: ReactNode;
}) {
  const t = useT();
  const tip = contourTip(props, massifName);

  return (
    <div className={styles.preview}>
      <div className={styles.previewMarks}>
        {props.land_users_count === 0 ? (
          <Badge tone="attention" title={t(m.noLandUserHint)}>
            {t(m.noLandUser)}
          </Badge>
        ) : null}
        {tip.shared ? (
          <Badge tone="attention" title={t(m.sharedHint)}>
            {t(m.sharedBadge, { count: props.shared_contours })}
          </Badge>
        ) : null}
        {tip.cropDemo ? <Badge tone="demo">{t(m.demoCrop)}</Badge> : null}
        <Badge tone={tip.tierKnown ? "derived" : "unknown"} title={t(m.tierHint)}>
          {t(m.tierBadge, { tier: tip.tier })}
        </Badge>
      </div>

      <dl className={styles.previewList}>
        <dt>{t(m.massif)}</dt>
        <dd className={tip.massifMissing ? styles.previewMuted : undefined}>
          {tip.massif}
        </dd>

        <dt>{t(m.landUser)}</dt>
        <dd className={tip.landUserMissing ? styles.previewMuted : undefined}>
          {tip.landUser}
        </dd>

        <dt>{t(m.registryArea)}</dt>
        <dd className={tip.areaMissing ? styles.previewMuted : undefined}>
          {tip.area}
        </dd>

        <dt>{t(m.relation)}</dt>
        <dd>
          {tip.relation}
          {tip.pieces > 1 ? ` · ${t(m.pieces, { count: tip.pieces })}` : ""}
        </dd>

        {tip.cropDemo ? (
          <>
            <dt>{t(m.cropNow)}</dt>
            <dd className={styles.previewDemo}>
              {tip.cropDemo.label} · {tip.cropDemo.role} · {tip.cropDemo.area}
              {tip.cropDemo.crops > 1
                ? ` · ${t(m.cropCount, { count: tip.cropDemo.crops })}`
                : ""}
            </dd>
          </>
        ) : null}
      </dl>

      {links ? <div className={styles.previewLinks}>{links}</div> : null}

      <p className={styles.previewNote}>
        {t(m.note)}
        {tip.shared ? ` ${t(m.noteShared)}` : ""}
      </p>
    </div>
  );
}

/**
 * Переход на полный уровень контура. Вынесен отдельно, потому что живёт в
 * закреплённой нижней строке панели, а не в теле превью, — и потому что
 * контекст анализа обязан ехать вместе с адресом.
 */
export function MapPreviewAction({
  contourId,
  linkSearch,
}: {
  contourId: number;
  linkSearch: (extra?: Record<string, string | number>) => string;
}) {
  const t = useT();
  return (
    <Link
      to={{ pathname: levelPath("contour", contourId), search: linkSearch() }}
    >
      <Button variant="primary">{t(m.open)}</Button>
    </Link>
  );
}

/**
 * Переходы на соседние уровни прямо из превью.
 *
 * Массив — настоящий уровень: у фичи есть его идентификатор, и ссылка ведёт
 * ровно туда. Хозяйство — НЕ уровень: `/map/contours` отдаёт по контуру только
 * ИМЯ землепользователя, идентификатора у фичи нет. Поэтому переход честно
 * назван поиском по реестру, а не «карточкой хозяйства»: ссылка, которая
 * обещает уровень и приводит к списку совпадений, хуже отсутствующей.
 */
export function MapPreviewLevelLinks({
  props,
  massifName,
  linkSearch,
}: {
  props: ContourFeatureProps;
  massifName: string | null;
  linkSearch: (extra?: Record<string, string | number>) => string;
}) {
  const t = useT();
  return (
    <>
      <Link
        to={{
          pathname: levelPath("massif", props.massif_id),
          search: linkSearch(),
        }}
      >
        {t(m.massifLink, { name: massifName ?? `#${props.massif_id}` })}
      </Link>

      {props.land_users_count === 0 ? (
        <span className={styles.previewMuted}>
          {t(m.noOneToFind)}
        </span>
      ) : props.land_user ? (
        <Link
          to={{ pathname: ROUTES.actors, search: linkSearch({ q: props.land_user }) }}
        >
          {t(m.farmLink, { name: props.land_user })}
        </Link>
      ) : (
        <span className={styles.previewMuted}>
          {t(m.nameMissing, { count: props.land_users_count })}
        </span>
      )}

      {props.land_users_count > 1 && props.land_user ? (
        <span className={styles.previewMuted}>
          {t(m.oneOfMany, { count: props.land_users_count })}
        </span>
      ) : null}
    </>
  );
}
